<template>
  <div
    v-if="isOpen"
    class="member-benefits"
    role="dialog"
    aria-modal="true"
    aria-labelledby="member-benefits-title"
    @click.self="close"
  >
    <div class="member-benefits__card">
      <button
        type="button"
        class="member-benefits__close"
        aria-label="Close popup"
        @click="close"
      >
        <i class="icon icon-cross" />
      </button>

      <div class="member-benefits__media">
        <picture>
          <source media="(min-width: 992px)" :srcset="desktopImage" />
          <img class="member-benefits__image" :src="mobileImage" alt="" />
        </picture>
        <p class="member-benefits__caption">Members only</p>
      </div>

      <div class="member-benefits__body">
        <h2 id="member-benefits-title" class="member-benefits__title">
          Exclusive benefits
        </h2>
        <p class="member-benefits__copy">
          Earn points with every purchase to redeem against future orders, plus
          unlock benefits including early sale access, free shipping and
          exclusive concierge services.
        </p>
        <p class="member-benefits__copy">
          Join now for £20 off your first purchase.
        </p>

        <ul class="member-benefits__list">
          <li v-for="benefit in benefits" :key="benefit.title">
            <span class="member-benefits__icon" aria-hidden="true">
              <img
                v-if="benefit.icon === 'bag'"
                src="@/assets/images/member-benefits/bag.svg"
                alt=""
              />
              <span
                v-else-if="benefit.icon === 'gift'"
                class="member-benefits__gift"
              >
                <img
                  class="member-benefits__gift-bow"
                  src="@/assets/images/member-benefits/gift-v4.svg"
                  alt=""
                />
                <img
                  class="member-benefits__gift-ribbon"
                  src="@/assets/images/member-benefits/gift-v1.svg"
                  alt=""
                />
                <img
                  class="member-benefits__gift-tie"
                  src="@/assets/images/member-benefits/gift-v2.svg"
                  alt=""
                />
                <img
                  class="member-benefits__gift-box"
                  src="@/assets/images/member-benefits/gift-v3.svg"
                  alt=""
                />
              </span>
              <img
                v-else
                src="@/assets/images/member-benefits/heart.svg"
                alt=""
              />
            </span>
            <div class="member-benefits__benefit">
              <p class="member-benefits__benefit-title">{{ benefit.title }}</p>
              <p class="member-benefits__benefit-copy">{{ benefit.copy }}</p>
            </div>
          </li>
        </ul>

        <a class="member-benefits__cta" href="/account/register" @click="close">
          Sign up now
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

const DISMISS_KEY = "memberBenefitsPopupDismissed";

export default {
  name: "MemberBenefitsPopup",
  props: {
    blocked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      mobileImage:
        "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_3_1.jpg?v=1790942814",
      desktopImage:
        "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_2_a33724ea-e3e2-48dc-8dbf-afb3b0390861.jpg?v=1790942485",
      benefits: [
        {
          icon: "bag",
          title: "Make a purchase",
          copy: "Earn 1 point for every £1 spent",
        },
        {
          icon: "gift",
          title: "Celebrate a birthday",
          copy: "Celebrate with up to £50",
        },
        {
          icon: "heart",
          title: "Refer a friend",
          copy: "Refer a friend: gift £20, receive £20",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("auth", ["isLoggedIn"]),
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);

    if (localStorage.getItem(DISMISS_KEY) === "true") {
      return;
    }

    this.openTimer = window.setTimeout(() => {
      if (this.isLoggedIn || this.blocked) {
        return;
      }
      if (localStorage.getItem(DISMISS_KEY) === "true") {
        return;
      }
      if (!this.hasFinishedFirstVisit()) {
        return;
      }

      this.isOpen = true;
    }, 5 * 1000);
  },
  beforeUnmount() {
    window.clearTimeout(this.openTimer);
    window.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    hasFinishedFirstVisit() {
      const sawOption1 =
        localStorage.getItem("memberDiscountPopupDismissed") === "true";
      let signupClosed = false;

      try {
        const preferences = JSON.parse(
          localStorage.getItem("shopifyUserPreferences") || "{}"
        );
        signupClosed = !!preferences.dataCollectionDismissed;
      } catch (error) {
        signupClosed = false;
      }

      return sawOption1 || signupClosed;
    },
    close() {
      this.isOpen = false;
      localStorage.setItem(DISMISS_KEY, "true");
    },
    onKeydown(event) {
      if (this.isOpen && event.key === "Escape") {
        this.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.member-benefits {
  position: fixed;
  inset: 0;
  z-index: 1002;
  display: flex;
  align-items: stretch;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(10px);
}

.member-benefits__card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  background: #fff;
}

.member-benefits__close {
  position: absolute;
  top: 10px;
  right: 11px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #fff;
  color: #000;
  cursor: pointer;

  .icon {
    font-size: 12px;
  }
}

.member-benefits__media {
  position: relative;
  flex-shrink: 0;
}

.member-benefits__image {
  display: block;
  width: 100%;
  height: 249px;
  object-fit: cover;
  object-position: center 30%;
}

.member-benefits__caption {
  display: none;
}

.member-benefits__body {
  flex: 1;
  overflow: auto;
  padding: 18px 20px 24px;
  text-align: center;
}

.member-benefits__title {
  margin: 0 0 16px;
  @include editorial-headline(24px);
  line-height: 1;
  text-transform: uppercase;
  color: #000;
}

.member-benefits__copy {
  margin: 0 0 16px;
  @include editorial-body(13px);
  line-height: 1.5;
  color: #000;
}

.member-benefits__list {
  margin: 8px 0 20px;
  padding: 0;
  list-style: none;
  text-align: left;
}

.member-benefits__list li {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.member-benefits__icon {
  position: relative;
  display: block;
  flex-shrink: 0;
  width: 52px;
  height: 52px;

  img {
    display: block;
  }
}

.member-benefits__gift {
  position: relative;
  width: 52px;
  height: 52px;

  img {
    position: absolute;
    display: block;
    max-width: none;
  }
}

.member-benefits__gift-bow {
  top: 5.875px;
  left: 10.42px;
}

.member-benefits__gift-ribbon {
  top: 16.71px;
  left: 6.08px;
}

.member-benefits__gift-tie {
  top: 16.71px;
  left: 25.58px;
}

.member-benefits__gift-box {
  top: 25.38px;
  left: 10.42px;
}

.member-benefits__benefit {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  min-height: 88px;
  padding: 16px 20px;
  border-radius: 4px;
  background: #f8f8f8;
}

.member-benefits__benefit-title {
  margin: 0;
  @include editorial-body(14px);
  line-height: 1.5;
  text-transform: uppercase;
  color: #000;
}

.member-benefits__benefit-copy {
  margin: 0;
  @include utility-body(14px);
  line-height: 1.5;
  color: #000;
}

.member-benefits__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  padding: 15px 28px;
  border-radius: 4px;
  background: #000;
  color: #fff;
  text-decoration: none;
  @include utility-body(13px);
  line-height: 1.35;
  text-transform: uppercase;
}

@include respond-above(lg) {
  .member-benefits {
    align-items: center;
    padding: 24px;
  }

  .member-benefits__card {
    flex-direction: row;
    width: min(1014px, calc(100vw - 48px));
    height: min(680px, calc(100vh - 48px));
    max-height: calc(100vh - 48px);
  }

  .member-benefits__close {
    top: 20px;
    right: 20px;
    width: 20px;
    height: 20px;
    border-radius: 0;
    background: none;
  }

  .member-benefits__media {
    width: 50%;
    height: 100%;
  }

  .member-benefits__media::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);
  }

  .member-benefits__image {
    height: 100%;
    object-position: center 20%;
  }

  .member-benefits__caption {
    position: absolute;
    right: 0;
    bottom: 48px;
    left: 0;
    z-index: 1;
    display: block;
    margin: 0;
    color: #fff;
    text-align: center;
    @include editorial-headline(40px);
    line-height: 1;
    text-transform: uppercase;
  }

  .member-benefits__body {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 50%;
    padding: 40px 48px 36px;
    overflow: auto;
  }

  .member-benefits__title {
    @include editorial-headline(32px);
  }

  .member-benefits__copy {
    @include editorial-body(14px);
  }

  .member-benefits__list li {
    gap: 20px;
  }
}
</style>
